// @flow

/**
 * Module dependencies.
 */

import {
  colors,
  defaultTransition,
  keyframes,
  media,
  states,
  units
} from 'client/styles';

import { ifProp } from 'styled-tools';
import { translator } from 'core/utils';
import RawHtml from 'react-raw-html';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import cookies from 'browser-cookies';
import styled, { css } from 'styled-components';

/**
 * User consent key.
 */

const userConsentKey = 'user-consent';

/**
 * `Props` type.
 */

type Props = {|
  translate: Function
|};

/**
 * `State` type.
 */

type State = {|
  isVisible: boolean
|};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  animation: ${keyframes.fadeOut} ${defaultTransition} both;
  bottom: 18px;
  display: grid;
  grid-template-columns: 1fr 10fr 1fr;
  pointer-events: none;
  position: fixed;
  transition: visibility ${defaultTransition};
  visibility: hidden;
  width: 100%;
  z-index: 10;

  ${ifProp('isVisible', css`
    animation-name: ${keyframes.fadeIn};
    pointer-events: all;
    visibility: visible;
  `)}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  align-items: center;
  background-color: ${colors.shadesOfBlue.iron};
  border-radius: 2px;
  box-shadow: 0 30px 64px 0 rgba(46, 52, 56, 0.17), 0 12px 24px 0 rgba(46, 52, 56, 0.2);
  display: flex;
  flex-wrap: wrap;
  gap: ${units(3)};
  grid-column: 2;
  padding: ${units(1.5)} ${units(4)} ${units(1.5)} ${units(3)};

  ${media.min.xl`
    justify-content: space-between;
  `}
`;

/**
 * `ButtonsWrapper` styled component.
 */

const ButtonsWrapper = styled.div`
  display: flex;
`;

/**
 * `Button` styled component.
 */

const Button = styled.button`
  background-color: transparent;
  display: inline-block;
  font-weight: 600;
  height: 40px;
  line-height: 24px;
  overflow: hidden;
  padding: 4px ${units(3)} 9px;
  text-align: center;
  white-space: nowrap;

  ${states.action`
    cursor: pointer;
    outline: none;
  `};
`;

/**
 * `AcceptButton` styled component.
 */

const AcceptButton = styled(Button)`
  border: 2px solid ${colors.brandSecondary};
  border-radius: 100px;
  color: ${colors.brandSecondary};
  transition: ${defaultTransition};
  transition-property: background-color, color;

  ${states.interaction`
    background-color: ${colors.brandSecondary};
    color: ${colors.white};
  `};
`;

/**
 * `DeclineButton` styled component.
 */

const DeclineButton = styled(Button)`
  border: none;
  color: ${colors.brandSecondary};
  transition: color ${defaultTransition};

  ${states.interaction`
    color: ${colors.brandPrimary};
  `};
`;

/**
 * `CookiePrompt` component.
 */

class CookiePrompt extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * State.
   */

  state: State = {
    isVisible: true
  };

  /**
   * Handle decline cookies.
   */

  handleDeclineCookies = () => {
    cookies.set(userConsentKey, 'no');

    this.setState({ isVisible: false });
  }

  /**
   * Handle accept cookies.
   */

  handleAcceptCookies = () => {
    cookies.set(userConsentKey, 'yes');

    this.setState({ isVisible: false });
  }

  /**
   * Set initial state.
   */

  setInitialState = () => {
    this.setState({
      isVisible: !cookies.get(userConsentKey)
    });
  }

  /**
   * Component will mount.
   */

  componentWillMount() {
    this.setInitialState();
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const { translate } = this.props;

    return (
      <Wrapper isVisible={this.state.isVisible}>
        <Content>
          <Text.Paragraph color={colors.brandSecondary}>
            <RawHtml.span>
              {translate('cookiePrompt.description')}
            </RawHtml.span>
          </Text.Paragraph>

          <ButtonsWrapper>
            <AcceptButton
              onClick={this.handleAcceptCookies}
              type={'button'}
            >
              {translate('cookiePrompt.acceptButton')}
            </AcceptButton>

            <DeclineButton
              onClick={this.handleDeclineCookies}
              type={'button'}
            >
              {translate('cookiePrompt.declineButton')}
            </DeclineButton>
          </ButtonsWrapper>
        </Content>
      </Wrapper>
    );
  }

}

/**
 * Export `CookiePrompt` component.
 */

export default translator()(CookiePrompt);



// WEBPACK FOOTER //
// ../src/client/components/cookie-prompt/index.js